/**
 * Helpers for the runtime node-type registry's JSON Schemas — used to
 * auto-seed `WorkflowNode.outputSchema` and resolve Connect-mode field
 * lists without authors hand-copying Zod shapes into the graph editor.
 */

/**
 * Helpers for the runtime node-type registry's JSON Schemas — used to
 * auto-seed `WorkflowNode.outputSchema` and resolve Connect-mode field
 * lists without authors hand-copying Zod shapes into the graph editor.
 */

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * `zodToJsonSchema(schema, 'output')` wraps the object under
 * `{ $ref: '#/definitions/output', definitions: { output: … } }`.
 * The graph editor expects a flat `{ type: 'object', properties: … }`.
 */
export function normalizeRegistryOutputSchema(
  schema: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  return normalizeRegistryJsonSchema(schema);
}

/** Alias — same unwrapping for input and output registry schemas. */
export function normalizeRegistryJsonSchema(
  schema: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!schema || !isPlainObject(schema)) return null;

  const ref = schema.$ref;
  if (typeof ref === 'string' && ref.startsWith('#/definitions/')) {
    const name = ref.slice('#/definitions/'.length);
    const defs = schema.definitions;
    if (isPlainObject(defs)) {
      const resolved = defs[name];
      if (isPlainObject(resolved)) return resolved;
    }
  }

  if (isPlainObject(schema.properties)) return schema;

  const defs = schema.definitions;
  if (isPlainObject(defs)) {
    for (const candidate of Object.values(defs)) {
      if (isPlainObject(candidate) && isPlainObject(candidate.properties)) {
        return candidate;
      }
    }
  }

  return Object.keys(schema).length > 0 ? schema : null;
}

/** True when the persisted schema is missing or `{}` (legacy sentinel). */
export function isEmptyOutputSchema(schema: Record<string, unknown> | null | undefined): boolean {
  if (!schema || Object.keys(schema).length === 0) return true;
  const normalized = normalizeRegistryOutputSchema(schema);
  if (!normalized) return true;
  const props = normalized.properties;
  return !isPlainObject(props) || Object.keys(props).length === 0;
}

/** True when the node has a non-empty per-node output transform. */
export function hasCustomOutputMapping(outputMapping: string | null | undefined): boolean {
  return typeof outputMapping === 'string' && outputMapping.trim() !== '';
}

/**
 * Default output schema to persist for a node — `null` when the author
 * has customized output (mapping) or the type has no concrete schema
 * (llm-agent, passthrough, code-exec).
 */
export function defaultOutputSchemaForNode(
  nodeType: string,
  outputMapping: string | null | undefined,
  nodeTypeOutputSchemaByKey: ReadonlyMap<string, Record<string, unknown> | null>,
): Record<string, unknown> | null {
  if (nodeType === 'llm-agent') return null;
  if (hasCustomOutputMapping(outputMapping)) return null;
  const raw = nodeTypeOutputSchemaByKey.get(nodeType);
  const def = normalizeRegistryOutputSchema(raw ?? null);
  return def && !isEmptyOutputSchema(def) ? def : null;
}

/** Node types with a concrete registry output schema (read-only in the UI). */
export function hasBuiltInOutputSchema(
  nodeType: string,
  outputMapping: string | null | undefined,
  nodeTypeOutputSchemaByKey: ReadonlyMap<string, Record<string, unknown> | null>,
): boolean {
  return defaultOutputSchemaForNode(nodeType, outputMapping, nodeTypeOutputSchemaByKey) != null;
}
